import { DatePipe, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';

import { Project, Purchased } from '../schemas';
import { AgreementService, Auth, ProductService } from '../service';
import { ExtraHoursData, ExtraHoursForm } from './extra-hours-form';
import { InvoiceForm, InvoiceFormData } from './invoice-form';
import { RemoveHoursData, RemoveHoursDialog } from './remove-hours-dialog';

@Component({
  selector: 'lcm-purchased-hours-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatTableModule, MatIconModule, MatButtonModule, MatTooltipModule, DatePipe, DecimalPipe, MatToolbarModule],
  templateUrl: './purchased-hours-table.html',
})
export class PurchasedHoursTable {
  statuses = {
    0: 'Not ready to invoice',
    10: 'Ready to invoice',
    20: 'Invoiced',
  };

  ownerType = input.required<'agreement' | 'product'>();
  ownerId = input.required<string>();
  projects = input<Project[]>([]);

  now = signal(new Date().toISOString());

  agreements = inject(AgreementService);
  products = inject(ProductService);
  auth = inject(Auth);
  dialog = inject(MatDialog);

  removed = output();

  saved = output();

  columnsToShow = signal(['Hours', 'Value', 'Expires', 'Notes', 'DueDate', 'PurchaseOrder', 'Invoicing', 'actions']);

  service = computed(() => (this.ownerType() === 'agreement' ? this.agreements : this.products));

  source = input.required<Purchased[]>();

  trackBy(idx: number, row: Purchased) {
    return row.Id;
  }

  pending = input(false);

  remove(item: Purchased) {
    const data: RemoveHoursData = {
      id: item.Id,
      type: 'purchased',
      owner: this.ownerId(),
      service: this.service(),
    };
    this.dialog
      .open(RemoveHoursDialog, { data })
      .afterClosed()
      .subscribe({
        next: (result) => result && this.removed.emit(),
      });
  }

  edit(item: Purchased) {
    const data: ExtraHoursData = {
      item,
      title: 'Update purchased hours',
      owner: this.ownerId(),
      projects: this.projects(),
      service: this.service(),
      type: 'purchased',
    };
    this.dialog
      .open(ExtraHoursForm, { data })
      .afterClosed()
      .subscribe({
        next: (result) => result && this.saved.emit(),
      });
  }

  invoice(item: Purchased) {
    const data: InvoiceFormData = {
      item,
      ownerType: this.ownerType(),
      ownerId: this.ownerId(),
      projects: this.projects(),
      hoursType: 'purchased',
    };
    this.dialog
      .open(InvoiceForm, { data })
      .afterClosed()
      .subscribe({
        next: (result) => result && this.saved.emit(),
      });
  }
}
